import { RelaxationType } from './TargetingSpec/TargetingSpec';

export const placementTargetingPositions = {
  publisher_platforms: [
    'facebook',
    'instagram',
    'messenger',
    'audience_network',
  ] as const,
  // prettier-ignore
  facebook_positions: ['feed', 'right_hand_column', 'instant_article', 'video_feeds', 'marketplace', 'story', 'search'] as const,
  instagram_positions: ['stream', 'story', 'explore'] as const,
  audience_network_positions: [
    'classic',
    'instream_video',
    'rewarded_video',
  ] as const,
  messenger_positions: [
    'messenger_home',
    'sponsored_messages',
    'story',
  ] as const,
};

export type PublisherPositions = typeof placementTargetingPositions['publisher_platforms'][number];
export type FacebookPositions = typeof placementTargetingPositions['facebook_positions'][number];
export type InstagramPositions = typeof placementTargetingPositions['instagram_positions'][number];
export type AudienceNetworkPositions = typeof placementTargetingPositions['audience_network_positions'][number];
export type MessengerPositions = typeof placementTargetingPositions['messenger_positions'][number];

/**
 * @deprecated Use 'TargetingSpec' in '@marketing-milk/interface/src/facebook/audience/Targeting/TargetingSpec'
 * This Type is Deprecated, as it is badly named, inconsistent with facebooks API, and causes issues/misunderstandings on the front end.
 * Our goal is to iteratively replace this type, fixing the type errors that are caused slowly but surely.  This type cannot be deleted until
 * all instances of it are removed from our applications
 */
export interface FacebookTargetingSpec extends AdvancedTargetingSpec {
  age_max: number;
  age_min: number;
  genders?: Gender[];
  flexible_spec?: FlexibleTargetSpec[];
  exclusions?: FlexibleTargetSpec;
  geo_locations?: LocationTargetSpec;
  excluded_geo_locations?: LocationTargetSpec;
  publisher_platforms?: PublisherPositions[];
  facebook_positions?: FacebookPositions[];
  instagram_positions?: InstagramPositions[];
  audience_network_positions?: AudienceNetworkPositions[];
  messenger_positions?: MessengerPositions[];
  targeting_relaxation_types?: RelaxationType;
}

export interface FacebookTargetingSpecWithoutLocationTypes
  extends FacebookTargetingSpec {
  geo_locations?: Omit<LocationTargetSpec, 'location_types'>;
  excluded_geo_locations?: Omit<LocationTargetSpec, 'location_types'>;
}

export interface AdvancedTargetingSpec {
  interests?: DynamicTargetSpec[];
  behaviors?: DynamicTargetSpec[];
  life_events?: DynamicTargetSpec[];
  custom_audiences?: CustomAudienceTargetSpec[];
  excluded_custom_audiences?: CustomAudienceTargetSpec[];
  relationship_statuses?: RelationshipStatusSpec[];
  education_statuses?: EducationStatusSpec[];
  college_years?: number[];
  education_majors?: DynamicTargetSpec[];
  work_employers?: DynamicTargetSpec[];
  work_positions?: DynamicTargetSpec[];
  industries?: DynamicTargetSpec[];
  income?: DynamicTargetSpec[];
  family_statuses?: DynamicTargetSpec[];
  education_schools?: DynamicTargetSpec[];
  // https://developers.facebook.com/docs/marketing-api/audiences/reference/targeting-search (adTargetingCategory, user_os)
  user_os?: string[];
  // https://developers.facebook.com/docs/marketing-api/audiences/reference/targeting-search (adTargetingCategory, user_device)
  user_device?: string[];
  // https://developers.facebook.com/docs/marketing-api/audiences/reference/targeting-search (adTargetingCategory, user_device)
  excluded_user_device?: string[];
  wireless_carrier?: string[];
  app_install_state?: AppInstallState;
  connections?: ConnectionSpec[];
  excluded_connections?: ConnectionSpec[];
  friends_of_connections?: ConnectionSpec[];
}

export interface FlexibleTargetSpec extends AdvancedTargetingSpec {
  education_schools?: DynamicTargetSpec[];
  home_value?: DynamicTargetSpec[];
  interested_in?: DynamicTargetSpec[];
  user_adclusters?: DynamicTargetSpec[];
}

export enum EducationStatusSpec {
  High_School = 1,
  Undergrad = 2,
  Alum = 3,
  High_School_Grade = 4,
  Some_College = 5,
  Associates_Degree = 6,
  In_Grad_School = 7,
  Some_Grade_School = 8,
  Masters_Degree = 9,
  Professional_Degree = 10,
  Doctorate_Degree = 11,
  Unspecified = 12,
  Some_High_School = 13,
}

export enum RelationshipStatusSpec {
  Single = 1,
  In_Relationship = 2,
  Married = 3,
  Engaged = 4,
  Not_Specified = 6,
  Civil_Union = 7,
  Domestic_Partnership = 8,
  Open_Relationship = 9,
  Complicated = 10,
  Separated = 11,
  Divorced = 12,
  Widowed = 13,
}

export enum AppInstallState {
  Installed = 'installed',
  Not_Installed = 'not_installed',
  None = 'none',
}
// Array of FB ids for fans of page, RSVPs, etc
export interface ConnectionSpec {
  id: string | number;
}

export enum Gender {
  All = 0,
  Male = 1,
  Female = 2,
}

export type DistanceUnit = 'mile' | 'kilometer';

export interface LocationTargetSpec {
  // Find countries from: https://developers.facebook.com/docs/reference/ads-api/get-autocomplete-data/#countries
  countries?: string[];
  cities?: LocationCityTargetSpec[];
  // Retrieve keys from: https://developers.facebook.com/docs/reference/ads-api/get-autocomplete-data/#regions
  regions?: LocationRegionTargetSpec[];
  // Retrieve keys from: https://developers.facebook.com/docs/reference/ads-api/get-autocomplete-data/#zipcode
  zips?: LocationZipCodeTargetSpec[];
  places?: LocationPlaceTargetSpec[];
  custom_locations?: LocationCustomTargetSpec[];
  geo_markets?: LocationGeoMarketTargetSpec[];
  electoral_district?: LocationElectoralDistrictTargetSpec[];
  location_types?: [LocationType.Home, LocationType.Recent];
  country_groups?: CountryGroup[];
}

export enum LocationType {
  Recent = 'recent', // Recently been in area
  Home = 'home', // Marked as home city on FB
}

export enum CountryGroup {
  worldwide,
  africa,
  afta,
  android_app_store,
  android_free_store,
  apec,
  asia_,
  caribbean,
  central_america,
  cisfta,
  eea,
  emerging_markets,
  europe,
  gcc,
  itunes_app_store,
  mercosur,
  nafta,
  north_america,
  oceania,
  south_america,
}

interface LocationCustomTargetSpecBase {
  name?: string;
  radius: number;
  distance_unit: DistanceUnit;
  latitude?: string;
  longitude?: string;
  address_string?: string;
  primary_city_id?: string | number;
  region_id?: string | number;
  country?: string;
}

export interface LocationCustomAddressTargetSpec
  extends LocationCustomTargetSpecBase {
  address_string: string;
}

export interface LocationCustomLatLongTargetSpec
  extends LocationCustomTargetSpecBase {
  latitude: string;
  longitude: string;
}

export type LocationCustomTargetSpec =
  | LocationCustomAddressTargetSpec
  | LocationCustomLatLongTargetSpec;

export interface LocationCityTargetSpec {
  country?: string;
  distance_unit: DistanceUnit;
  key: string;
  name?: string;
  radius: number;
  region?: string | number;
  region_id?: string;
}

export interface LocationRegionTargetSpec {
  key: string;
  name?: string;
  country?: string;
}

export interface LocationZipCodeTargetSpec {
  key: string;
  name?: string;
  primary_city_id?: string;
  region_id?: string;
  country?: string;
}

// Place documentation here: https://developers.facebook.com/docs/places
export interface LocationPlaceTargetSpec {
  key: string;
  name: string;
  radius: number;
  distance_unit: DistanceUnit;
  latitude?: string;
  longitude?: string;
}

// Retrieve keys from: https://developers.facebook.com/docs/marketing-api/audiences/reference/targeting-search#dmacode
export interface LocationGeoMarketTargetSpec {
  key: string;
  name: string;
}

// Retrieve keys from: https://developers.facebook.com/docs/marketing-api/audiences/reference/targeting-search#electoral
export interface LocationElectoralDistrictTargetSpec {
  key: string;
}

export const exampleLocationTargetSpec: LocationTargetSpec = {
  location_types: [LocationType.Home, LocationType.Recent],
  custom_locations: [
    {
      address_string: '906 Olive St',
      radius: 10,
      distance_unit: 'mile',
      latitude: '38.6288',
      longitude: '90.194',
    },
  ],
};

// A dynamic target spec can reference
// Different types of filters, etc on FB
// It goes off of what the id is
export interface DynamicTargetSpec {
  id: string;
  name?: string;
}

export interface CustomAudienceTargetSpec {
  id: string;
}

export const exampleFacebookTargetingSpec: FacebookTargetingSpec = {
  age_max: 21,
  age_min: 18,
  genders: [1],
  flexible_spec: undefined,
  exclusions: undefined,
  geo_locations: exampleLocationTargetSpec,
};
